import { type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { type CartItem, type PartialCartItem } from '@cart-services/types'
import { DeliveryClass } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { DIALOG_ADD_TO_CART_ERROR } from '../DialogAddToCartError/DialogAddToCartError'

export const DIALOG_BUY_AGAIN = 'DIALOG_BUY_AGAIN'

export interface DialogBuyAgainProps {
  campaignId: number | null
  items: PartialCartItem[]
  basePath: string
  platform: PlatformType
}

const useShowSnackbar = ({ handleGoToCart }: { handleGoToCart: () => void }) => {
  const { t } = useTranslation()
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  return () => {
    openSnackbar({
      message: t('wpOrder.addedSuccessfully', { defaultValue: '訂單商品已加入購物車！' }),
      severity: 'success',
      action: (
        <Button
          variant="text"
          color="blue"
          onClick={handleGoToCart}
        >
          {t('wpOrder.checkout', { defaultValue: '前往結帳' })}
        </Button>
      ),
    })
  }
}

const DialogBuyAgainBody = ({ handleBuyThisOrderOnly, handleAddToCart, onClose }: {
  handleBuyThisOrderOnly: () => void
  handleAddToCart: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()

  return (
    <>
      <DialogHeader align="center" title={t('wpOrder.repeatOrder', { defaultValue: '再買一次' })} onClose={onClose} />
      <DialogContent>
        <Text align="center" color={colors.grey800}>
          <Trans
            i18nKey="wpOrder.buyAgainText"
            components={{
              Br: <div />,
            }}
          />
        </Text>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleBuyThisOrderOnly}
          fullWidth
          size="large"
          variant="outlined"
          color="grey"
          whiteSpace="nowrap"
        >
          {t('wpOrder.buyThisOrderOnly', { defaultValue: '僅買此訂單' })}
        </Button>
        <Button
          onClick={handleAddToCart}
          fullWidth
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.addToCart', { defaultValue: '加入購物車' })}
        </Button>
      </DialogActions>
    </>
  )
}

const DialogBuyAgainComponent = () => {
  const { closeDialog, campaignId, items, basePath, platform } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)
  const { upsertCartItem, updateCartItems, items: cartItems } = useFpB2bCartService(campaignId ?? null, platform)
  const { openDialog: openDialogAddToCartError } = useDialog(DIALOG_ADD_TO_CART_ERROR)

  const history = useHistory()
  const showSnackbar = useShowSnackbar({
    handleGoToCart: () => {
      history.push(`${basePath}/cart`)
    },
  })

  const handleBuyThisOrderOnly = useCallbackEvent(() => {
    updateCartItems(items as CartItem[])
    closeDialog()
    showSnackbar()
  })

  const handleAddToCart = useCallbackEvent(() => {
    const hasFrozenDelivery = cartItems.some(item => item.delivery_class === DeliveryClass.FROZEN)
    const hasNormalDelivery = cartItems.some(item => item.delivery_class === DeliveryClass.NORMAL)
    const hasDeliveryClassConflict = items.some((item) => {
      const deliveryClass = item.delivery_class
      return (deliveryClass === DeliveryClass.FROZEN && hasNormalDelivery) || (deliveryClass === DeliveryClass.NORMAL && hasFrozenDelivery)
    })
    if (hasDeliveryClassConflict) {
      closeDialog()
      openDialogAddToCartError({ campaignId, items, basePath, platform })
      return
    }
    items.forEach(item => upsertCartItem(item))
    closeDialog()
    showSnackbar()
  })

  return (
    <DialogBuyAgainBody
      onClose={closeDialog}
      handleBuyThisOrderOnly={handleBuyThisOrderOnly}
      handleAddToCart={handleAddToCart}
    />
  )
}

export const DialogBuyAgain = () => {
  const { open, closeDialog } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)

  return (
    <Dialog onClose={closeDialog} open={open} maxWidth={320} fullWidth>
      {open && <DialogBuyAgainComponent />}
    </Dialog>
  )
}
